import React, { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector } from '../../../state/store';
import { themeProps } from '../../../styles/theme';
import TagConfigDialog from './TagConfigDialog';
import { setSensorsById } from '../../../state/actions';
import RemoveDialog from '../../Dialog/RemoveDialog';
import useSensorDetailsInLoc from './useSensorDetailsInLoc';
import { getCurrentLocation, getLocationsById } from '../../../state/selectors';
import { fetchSensorLatest, updateSensorProperties } from '../../../services/apiService';
import useLocAllSpaces from '../../Map/useLocAllSpaces';

function TagList(): JSX.Element {
  const dispatch = useAppDispatch();
  const [selectedTag, setSelectedTag] = useState('');
  const [showTagConfig, setShowTagConfig] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const sensorsDetailsInLoc = useSensorDetailsInLoc();
  const currentLocation = useAppSelector(getCurrentLocation);
  const locationsById = useAppSelector(getLocationsById);
  const allSpaces = useLocAllSpaces(true);

  // reset selected tag, if tag doesn't exist on location change
  useEffect(() => {
    const tagExists = !!allSpaces?.some((space) => space.title === selectedTag);
    if (!tagExists) setSelectedTag('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSpaces, currentLocation]);

  const handleDelete = async () => {
    const taggedSensors = sensorsDetailsInLoc.filter((sensor) =>
      sensor.tags?.some((item) => item === selectedTag)
    );

    setIsDeleting(true);
    try {
      await Promise.all(
        taggedSensors.map(async (sensor) => {
          try {
            // fetch latest detail
            const sensorDetails = await fetchSensorLatest(sensor.id);
            if (!sensorDetails?.tags) return;

            const updatedTags = sensorDetails.tags.filter((tag) => tag !== selectedTag);
            const response = await updateSensorProperties(sensor.id, { tags: updatedTags });
            if (response.id) {
              const updatedSensorDetails = { ...sensor, ...response };
              dispatch(setSensorsById([updatedSensorDetails]));
            }
          } catch (error) {
            enqueueSnackbar(`Failed to remove tag from sensor ${sensor.id}, ${error}`, {
              variant: 'error',
              persist: true,
            });
          }
        })
      );
    } finally {
      setIsDeleting(false);
      setSelectedTag('');
      setShowRemoveDialog(false);
    }
  };

  const getRemoveDialogText = () => {
    let text = '';
    if (selectedTag) {
      const sensorsLength = allSpaces?.find((space) => space.title === selectedTag)?.sensors.length;
      const locName = locationsById.get(currentLocation)?.name;
      text = `${selectedTag} with ${sensorsLength} sensors from ${locName}`;
    }
    return text;
  };

  return (
    <Box>
      <RemoveDialog
        showDialog={showRemoveDialog}
        title={getRemoveDialogText()}
        removeHandler={handleDelete}
        cancelHandler={() => {
          setShowRemoveDialog(false);
          setSelectedTag('');
        }}
      />
      <List>
        <ListItem sx={{ margin: '5px' }}>
          <Grid container>
            <Grid size={{ sm: 1 }} />
            <Grid size={{ sm: 4 }} sx={{ alignSelf: 'center' }}>
              <b>Name</b>
            </Grid>
            <Grid size={{ sm: 3 }} sx={{ alignSelf: 'center' }}>
              <b>No. of Sensors</b>
            </Grid>
            <Grid size={{ sm: 3 }}>
              <Button
                variant={themeProps.btnVariant.secondary}
                onClick={() => setShowTagConfig(true)}
              >
                Add Tag
              </Button>
            </Grid>
          </Grid>
        </ListItem>
        <hr />
        {allSpaces?.map((space) => (
          <ListItem key={space.title} sx={{ margin: '5px' }}>
            <Grid container>
              <Grid size={{ sm: 1 }} />
              <Grid size={{ sm: 4 }}>{space.title}</Grid>
              <Grid size={{ sm: 3 }}>{space.sensors.length}</Grid>
              <Grid size={{ sm: 3 }}>
                <IconButton
                  aria-label={`Edit ${space.title}`}
                  disabled={isDeleting}
                  onClick={() => {
                    setShowTagConfig(true);
                    setSelectedTag(space.title);
                  }}
                >
                  <EditIcon color="success" />
                </IconButton>

                <IconButton
                  aria-label={`Delete ${space.title}`}
                  disabled={isDeleting}
                  onClick={() => {
                    setSelectedTag(space.title);
                    setShowRemoveDialog(true);
                  }}
                  sx={{ marginLeft: '5px' }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Grid>
            </Grid>
          </ListItem>
        ))}
      </List>
      {(!allSpaces || allSpaces.length === 0) && (
        <ListItem>
          <Alert severity="error" sx={{ width: '100%' }}>
            No spaces found
          </Alert>
        </ListItem>
      )}
      {showTagConfig && (
        <TagConfigDialog
          showDialog={showTagConfig}
          closeDialog={() => {
            setShowTagConfig(false);
            setSelectedTag('');
          }}
          selectedTag={selectedTag}
          setSelectedTag={setSelectedTag}
          allSpaces={allSpaces}
        />
      )}
    </Box>
  );
}

export default TagList;
